import { useState, useEffect } from 'react';
import { Play, Loader, Copy, Check, ChevronDown, ChevronUp, User, KeyRound, Shield } from 'lucide-react';
import type { SavedQuery } from '../../../types/query';
import type { ApiUser } from '../../../services/apiService';
import { PUBLIC_API_URL } from '../../../config/api';
import { apiService } from '../../../services/apiService';
import { permissionService } from '../../../services/permissionService';

const BASEPATH = `${process.env.REACT_APP_API_URL}`
interface SwaggerTryItPanelProps {
  query: SavedQuery;
}

type AuthMode = 'user' | 'manual';

interface TryItResponse {
  status: number;
  statusText: string;
  headers: Record<string, string>;
  body: string;
  time: number;
}

export default function SwaggerTryItPanel({ query }: SwaggerTryItPanelProps) {
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TryItResponse | null>(null);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [showHeaders, setShowHeaders] = useState(false);

  const [authMode, setAuthMode] = useState<AuthMode>('user');
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [manualToken, setManualToken] = useState('');
  const [manualPersonalKey, setManualPersonalKey] = useState('');

  useEffect(() => {
    loadUsers();
  }, [query.id]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const [allUsers, permissions] = await Promise.all([
        apiService.getUsers(),
        permissionService.getServicePermissions(query.id),
      ]);
      const allowedGuids = new Set(permissions.map(p => p.user_guid));
      const filtered = allUsers.filter(u => allowedGuids.has(u.guid));
      setUsers(filtered);
      if (filtered.length > 0) {
        setSelectedUserId(filtered[0].guid);
      } else {
        setSelectedUserId('');
      }
    } catch {
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getAuthHeaders = (): { Authorization: string; 'personal-key': string } | null => {
    if (authMode === 'user') {
      const user = users.find(u => u.guid === selectedUserId);
      if (!user) return null;
      return {
        'Authorization': `Bearer ${user.access_token}`,
        'personal-key': user.personal_key || '',
      };
    }
    return {
      'Authorization': manualToken.startsWith('Bearer ') ? manualToken : `Bearer ${manualToken}`,
      'personal-key': manualPersonalKey,
    };
  };

  const baseUrl = `${PUBLIC_API_URL}/v2/${query.endpoint_slug}`;
  const basepathUrl = `${BASEPATH}/api/services/test/${query.endpoint_slug}`;

  const buildUrl = (basepath = false): string => {
    const params = new URLSearchParams();
    Object.entries(filterValues).forEach(([key, value]) => {
      if (value.trim()) params.append(key, value.trim());
    });
    const qs = params.toString();
    return qs ? `${basepath ? basepathUrl : baseUrl}?${qs}` : basepath ? basepathUrl : baseUrl;
  };

  const buildCurl = (): string => {
    const auth = getAuthHeaders();
    let cmd = `curl -X POST "${buildUrl()}"`;
    cmd += ` \\\n  -H "Accept: application/json"`;
    if (auth) {
      cmd += ` \\\n  -H "Authorization: ${auth.Authorization}"`;
      cmd += ` \\\n  -H "personal-key: ${auth['personal-key']}"`;
    }
    return cmd;
  };

  const handleCopyCurl = async () => {
    try {
      await navigator.clipboard.writeText(buildCurl());
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2000);
    } catch { /* ignore */ }
  };

  const isAuthValid = (): boolean => {
    if (authMode === 'user') return !!selectedUserId;
    return manualToken.trim().length > 0 && manualPersonalKey.trim().length > 0;
  };

  const handleExecute = async () => {
    setIsLoading(true);
    setResponse(null);

    const auth = getAuthHeaders();
    const requestHeaders: Record<string, string> = { 'Accept': 'application/json' };
    if (auth) {
      requestHeaders['Authorization'] = auth.Authorization;
      requestHeaders['personal-key'] = auth['personal-key'];
    }

    const start = performance.now();
    try {
      const res = await fetch(buildUrl(true), {
        method: 'POST',
        headers: requestHeaders,
      });
      const elapsed = Math.round(performance.now() - start);
      const body = await res.text();

      const headers: Record<string, string> = {};
      res.headers.forEach((value, key) => {
        headers[key] = value;
      });

      let formattedBody = body;
      try {
        formattedBody = JSON.stringify(JSON.parse(body), null, 2);
      } catch { /* not json */ }

      setResponse({
        status: res.status,
        statusText: res.statusText,
        headers,
        body: formattedBody,
        time: elapsed,
      });
    } catch (err) {
      const elapsed = Math.round(performance.now() - start);
      setResponse({
        status: 0,
        statusText: 'Network Error',
        headers: {},
        body: err instanceof Error ? err.message : 'Erro de conexao',
        time: elapsed,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-green-700 bg-green-100';
    if (status >= 400 && status < 500) return 'text-amber-700 bg-amber-100';
    return 'text-red-700 bg-red-100';
  };

  const selectedUser = users.find(u => u.guid === selectedUserId);

  return (
    <div className="space-y-5">
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
          <Shield className="w-4 h-4 text-gray-600" />
          <h4 className="text-sm font-semibold text-gray-700">Autenticacao</h4>
          <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-semibold uppercase">Obrigatorio</span>
        </div>

        <div className="p-4 space-y-4">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAuthMode('user')}
              className={`flex items-center gap-2 px-3.5 py-2 text-sm rounded-lg border transition-colors ${
                authMode === 'user'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <User className="w-3.5 h-3.5" />
              Selecionar usuario
            </button>
            <button
              type="button"
              onClick={() => setAuthMode('manual')}
              className={`flex items-center gap-2 px-3.5 py-2 text-sm rounded-lg border transition-colors ${
                authMode === 'manual'
                  ? 'bg-blue-50 border-blue-300 text-blue-700 font-medium'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <KeyRound className="w-3.5 h-3.5" />
              Inserir manualmente
            </button>
          </div>

          {authMode === 'user' && (
            <div className="space-y-3">
              {loadingUsers ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                  <Loader className="w-4 h-4 animate-spin" />
                  Carregando usuarios...
                </div>
              ) : users.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">Nenhum usuario com permissao para este servico.</p>
              ) : (
                <>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Selecione um usuario...</option>
                    {users.map(user => (
                      <option key={user.guid} value={user.guid}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>

                  {selectedUser && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Authorization (Bearer)</div>
                        <code className="text-xs text-gray-700 font-mono break-all line-clamp-2">
                          {selectedUser.access_token || '(vazio)'}
                        </code>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Personal-Key</div>
                        <code className="text-xs text-gray-700 font-mono break-all line-clamp-2">
                          {selectedUser.personal_key || '(vazio)'}
                        </code>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {authMode === 'manual' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Authorization (Bearer Token)
                </label>
                <input
                  type="text"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="seu-token-aqui"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Personal-Key
                </label>
                <input
                  type="text"
                  value={manualPersonalKey}
                  onChange={(e) => setManualPersonalKey(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                  placeholder="sua-personal-key-aqui"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {query.filter_columns.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">Parameters</h4>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2">
              <div className="col-span-3 text-xs font-semibold text-gray-500 uppercase">Nome</div>
              <div className="col-span-9 text-xs font-semibold text-gray-500 uppercase">Valor</div>
            </div>
            {query.filter_columns.map(col => (
              <div key={col} className="grid grid-cols-12 gap-0 px-4 py-2 border-b border-gray-100 last:border-0 items-center">
                <div className="col-span-3">
                  <span className="text-sm font-mono text-gray-800">{col}</span>
                  <span className="ml-1.5 text-[10px] text-gray-400 italic">string</span>
                </div>
                <div className="col-span-9">
                  <input
                    type="text"
                    value={filterValues[col] || ''}
                    onChange={(e) => setFilterValues(prev => ({ ...prev, [col]: e.target.value }))}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                    placeholder={`Filtrar por ${col}...`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleExecute}
          disabled={isLoading || !isAuthValid()}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors shadow-sm"
          title={!isAuthValid() ? 'Preencha os campos de autenticacao' : ''}
        >
          {isLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Execute
        </button>
        <button
          type="button"
          onClick={handleCopyCurl}
          className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {copiedCurl ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
          cURL
        </button>
      </div>

      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-gray-500 uppercase">Request URL</span>
        </div>
        <code className="text-sm text-blue-700 break-all font-mono">{buildUrl()}</code>
      </div>

      {response && (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold text-gray-500 uppercase">Response</span>
              <span className={`px-2 py-0.5 rounded text-xs font-bold ${getStatusColor(response.status)}`}>
                {response.status} {response.statusText}
              </span>
            </div>
            <span className="text-xs text-gray-500">{response.time}ms</span>
          </div>

          <button
            type="button"
            onClick={() => setShowHeaders(!showHeaders)}
            className="w-full flex items-center justify-between px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 transition-colors border-b border-gray-100"
          >
            <span className="uppercase font-semibold">Headers ({Object.keys(response.headers).length})</span>
            {showHeaders ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>

          {showHeaders && (
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 space-y-0.5">
              {Object.entries(response.headers).map(([key, value]) => (
                <div key={key} className="flex gap-2 text-xs font-mono">
                  <span className="text-gray-500">{key}:</span>
                  <span className="text-gray-800 break-all">{value}</span>
                </div>
              ))}
            </div>
          )}

          <div className="relative">
            <pre className="p-4 text-sm font-mono overflow-x-auto max-h-96 overflow-y-auto text-gray-800 bg-white leading-relaxed">
              {response.body}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
